import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { getEnhancementGateState } from '@src/utility/enhancementGate';
import { isCompressedRenditionPending } from '@src/utility/storyReadiness';

import type { EnhancementGateState } from '@src/utility/enhancementGate';

/**
 * PLAT-11227 — publish-gate hook for the publish/export surfaces (Creator
 * Studio Publish button, Content Studio export menu).
 *
 * - `enhancing`: corrected renditions are still being produced — the surface
 *   disables with the "Finishing audio enhancement…" tooltip.
 * - `failed`: terminal Auphonic failure — the surface stays enabled, but the
 *   first publish attempt shows the consent-downgrade prompt ("publish with
 *   original audio?"); publishing proceeds only through its explicit action.
 *   Consent is remembered per story for the session.
 */

/** Stories whose user already confirmed publishing with original audio. */
const consentedStories = new Set<string>();

export interface EnhancementPublishGate {
  state: EnhancementGateState;
  /**
   * True while publishing must be disabled: the video renditions are still
   * being produced (audio-first editing opens the editor before they exist —
   * publishing then would render the audio-only composition), or corrected
   * renditions are pending.
   */
  isBlocked: boolean;
  /** Tooltip copy for the disabled state; undefined otherwise. */
  tooltip?: string;
  /** Wraps a publish callback with the consent-downgrade flow. */
  guardPublish(publish: () => void): () => void;
}

export function useEnhancementPublishGate(): EnhancementPublishGate {
  const videoCreator = useVideoCreatorStore();
  const state = getEnhancementGateState(videoCreator.story);
  const storyId = videoCreator.storyId;
  const renditionPending =
    !!videoCreator.story && isCompressedRenditionPending(videoCreator.story);

  const guardPublish = (publish: () => void) => () => {
    if (state === 'failed' && storyId && !consentedStories.has(storyId)) {
      videoCreator.showToast({
        state: 'warning',
        message: 'Audio enhancement failed — publish with original audio?',
        action: {
          label: 'Publish Anyway',
          onClick: () => {
            consentedStories.add(storyId);
            publish();
          },
        },
      });
      return;
    }
    publish();
  };

  return {
    state,
    isBlocked: renditionPending || state === 'enhancing',
    tooltip: renditionPending
      ? 'Video is still processing'
      : state === 'enhancing'
        ? 'Finishing audio enhancement…'
        : undefined,
    guardPublish,
  };
}
